import { CursorClick, Globe, User, UsersThree } from '@phosphor-icons/react'
import { deleteRequest, getRequest, patchRequest, postRequest, putRequest } from '../core/api-client'
import { FileTypeOptions } from '../types/common/files'
import {
  DynamicSegmentDimensionType,
  DynamicSegmentFieldType,
  DynamicSegmentFilterType,
  DynamicSegmentFormStateType,
  DynamicSegmentOperatorInfoType,
  DynamicSegmentSchemaDataType,
  DynamicSegmentUIFilterType,
  CustomerTagRuleActionType,
  CustomerTagRuleOperatorType,
  CustomerTagSegmentFormStateType,
  SegmentConstructType,
  SegmentSortOrderType,
  StaticSegmentFormStateType
} from '../types/segment'
import { apiURI } from './constants'
import { generateUUID } from './helpers'
import { defaultPreviewFilters } from 'src/assets/SegmentsConfig'

export const createSegment = async (type: SegmentConstructType, merchantId: string, payload: any) => {
  try {
    const response = await postRequest(
      type === 'STATIC'
        ? `/merchant/${merchantId}/segments`
        : type === 'CUSTOMER_TAG'
          ? `/merchant/${merchantId}/segments/customer-tag`
          : `/merchant/${merchantId}/segments/audience`,
      apiURI.SEGMENTS_ENGINE,
      payload
    )
    return response
  } catch (e) {
    console.error(e)
    throw e
  }
}

/**
 * Get segment by ID
 *
 * @param  {string | undefined} merchantId       The Merchant ID
 *
 * @param  {string} segmentId                   The segment id whose details are to be fetched
 *
 */

export const getSegmentById = (merchantId: string | undefined, segmentId: string) => {
  try {
    const response = getRequest(`/merchant/${merchantId}/segments/${segmentId}`, apiURI.SEGMENTS_ENGINE)
    return response
  } catch (e) {
    console.error(e)
    return
  }
}

/**
 * Get template by ID
 *
 * @param  {string | undefined} merchantId       The Merchant ID
 *
 * @param  {string} templateId                   The template id whose details are to be fetched
 *
 */

export const getTemplateById = (merchantId: string | undefined, templateId: string) => {
  try {
    const response = getRequest(`/merchant/${merchantId}/segments/templates/${templateId}`, apiURI.SEGMENTS_ENGINE)
    return response
  } catch (e) {
    console.error(e)
    return
  }
}

export const editSegment = async (type: SegmentConstructType, merchantId: string, segmentId: string, payload: any) => {
  try {
    const response = await patchRequest(
      type === 'STATIC'
        ? `/merchant/${merchantId}/segments/${segmentId}`
        : type === 'CUSTOMER_TAG'
          ? `/merchant/${merchantId}/segments/${segmentId}/customer-tag`
          : `/merchant/${merchantId}/segments/${segmentId}/audience`,
      apiURI.SEGMENTS_ENGINE,
      payload
    )
    return response
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const toggleSegmentStatusById = async (merchantId: string, segmentId: string) => {
  try {
    const response = await putRequest(`/merchant/${merchantId}/segments/${segmentId}/toggle`, apiURI.SEGMENTS_ENGINE)
    return response
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const deleteSegmentById = async (merchantId: string, segmentId: string) => {
  try {
    const response = await deleteRequest(`/merchant/${merchantId}/segments/${segmentId}`, apiURI.SEGMENTS_ENGINE)
    return response
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const createManualSegmentPayload = (segmentFormState: StaticSegmentFormStateType) => {
  return {
    name: segmentFormState?.name,
    description: segmentFormState?.description,
    type: segmentFormState?.segmentType,
    file_meta: {
      file_url: segmentFormState?.currentFile?.url,
      file_name: segmentFormState?.currentFile?.name,
      size: segmentFormState?.currentFile?.size
    },
    segment_inclusion_rules: [
      ...(segmentFormState?.inclusionList?.map(item => ({
        type: 'STATE_CODE',
        action: item.type,
        values: item.values
      })) || []),
      ...(Boolean(segmentFormState?.manualInput)
        ? [
            {
              type: segmentFormState?.segmentType === 'PINCODE' ? 'PINCODE' : 'PHONE_NUMBER',
              action: 'INCLUDE',
              values: segmentFormState?.manualInput.split('\n').filter(line => line.trim())
            }
          ]
        : [])
    ],

    metadata: {
      manual_input: segmentFormState?.manualInput,
      inclusion_list: segmentFormState?.inclusionList,
      ...(Boolean(segmentFormState?.currentFile)
        ? {
            file_meta: {
              file_url: segmentFormState?.currentFile?.url,
              file_name: segmentFormState?.currentFile?.name,
              size: segmentFormState?.currentFile?.size,
              count:
                segmentFormState?.currentFile?.type === FileTypeOptions.CSV
                  ? segmentFormState?.currentFile?.count
                  : undefined,
              type: segmentFormState?.currentFile?.type
            }
          }
        : {})
    }
  }
}

export const parseManualSegmentConfig = (data: any): StaticSegmentFormStateType => {
  return {
    name: data?.name,
    description: data?.description,
    sourceType: 'CSV',
    ...(Boolean(data?.metadata?.file_meta)
      ? {
          currentFile: {
            name: data?.metadata?.file_meta?.file_name,
            url: data?.metadata?.file_meta?.file_url,
            count: data?.metadata?.file_meta?.count,
            size: data?.metadata?.file_meta?.size,
            type: FileTypeOptions.CSV
          }
        }
      : {}),
    inclusionList: data?.metadata?.inclusion_list ?? [],
    segmentType: data?.type ?? 'PHONE_NUMBER',
    manualInput: data?.metadata?.manual_input
      ?? (data?.segment_inclusion_rules ?? [])
          .filter((r: any) => r.type === 'PHONE_NUMBER' || r.type === 'PINCODE')
          .flatMap((r: any) => r.values ?? [])
          .join('\n')
  }
}

export const getDynamicSegmentIcon = (key: string) => {
  switch (key) {
    case 'USER_ATTRIBUTES':
      return User
    case 'EVENT_ATTRIBUTES':
      return CursorClick
    case 'NETWORK_ATTRIBUTES':
      return Globe
    case 'OTHERS':
      return UsersThree
    default:
      return UsersThree
  }
}

export const getSegmentSortQueryParam = (sortOrder: SegmentSortOrderType) => {
  switch (sortOrder) {
    case 'NEWEST':
      return '&sort_by=created_at&sort_direction=DESC'
    case 'OLDEST':
      return '&sort_by=created_at&sort_direction=ASC'
    case 'NAME_ASC':
      return '&sort_by=name&sort_direction=ASC'
    case 'NAME_DESC':
      return '&sort_by=name&sort_direction=DESC'
  }
}

const humanizeEventKey = (key: string): string =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())

export const mapEventData = (eventKey: string, metadata: any): { name: string; description: string } => {
  const eventCosmeticData = metadata.event_cosmetic_data
  return {
    name: eventCosmeticData?.[eventKey]?.name || humanizeEventKey(eventKey),
    description: eventCosmeticData?.[eventKey]?.description || ''
  }
}

/** Parent metric field for event / multi rows — the "times" count pill */
export const isEventMetricCountField = (field: DynamicSegmentFieldType): boolean =>
  field.columnType === 'AGGREGATED' && typeof field.id === 'string' && field.id.endsWith('_SYS_COUNT')

/** True when a filter value slot is unset; `0` / `'0'` are valid (e.g. "exactly 0 times") */
export const isDynamicSegmentFilterValueSlotEmpty = (v: unknown): boolean =>
  v === undefined || v === null || (typeof v === 'string' && v.trim() === '')

export const getDynamicSegmentUIFilterType = (field: DynamicSegmentFieldType): DynamicSegmentUIFilterType => {
  if (
    field.relationship !== 'ONE_ONE' &&
    field.category === 'EVENT_ATTRIBUTES' &&
    field.tableId === 'FCT__FLATTENED_EVENTS'
  ) {
    return 'EVENT'
  }
  if (field.relationship === 'ONE_ONE') {
    return 'SINGLE'
  }
  return 'MULTI'
}

/**
 * Converts DynamicSegmentFilterType[] to audience definition payload format
 *
 * @param filters - Array of DynamicSegmentFilterType to convert
 * @returns Audience definition object with "and" array
 */
export const convertFiltersToAudienceDefinition = (filters: DynamicSegmentFilterType[]) => {
  const convertFilter = (filter: DynamicSegmentFilterType): any => {
    const baseFilter: any = {
      operator: filter.operator.key,
      target: {
        field_id: filter.field.id,
        column_reference: filter.field.columnReference,
        table_name: filter.field.tableId
      },
      value: filter.values
    }

    // If filter has children (additional_metrics for EVENT type filters)
    if (filter.children && filter.children.length > 0) {
      baseFilter.additional_metrics = filter.children.map(child => ({
        operator: child.operator.key,
        target: {
          field_id: child.field.id,
          column_reference: child.field.columnReference,
          table_name: child.field.tableId
        },
        value: child.values,
        table_name: child.field.tableId
      }))
    }

    return baseFilter
  }

  return {
    and: filters.map(filter => convertFilter(filter))
  }
}

/**
 * Creates a dynamic segment payload from form state
 *
 * @param formState - DynamicSegmentFormStateType containing segment details
 * @param relationId - The relation ID for the segment
 * @returns Payload object for creating/updating a dynamic segment
 */
export const createDynamicSegmentPayload = (formState: DynamicSegmentFormStateType, relationId: string) => {
  const audienceDefinition = convertFiltersToAudienceDefinition(formState.filters)

  return {
    relation_id: relationId,
    name: formState.name,
    description: formState.description,
    tags: formState.tags,
    audience_definition: audienceDefinition,
    is_active: formState.isActive,
    metadata: {
      preview_filters: formState.previewFilters
    }
  }
}

/**
 * Parses segment API response to DynamicSegmentFormStateType
 *
 * @param segmentResponse - Segment API response object
 * @param schemaData - DynamicSegmentSchemaDataType containing field definitions
 * @returns DynamicSegmentFormStateType reconstructed from the response
 */
export const parseSegmentResponseToFormState = (
  segmentResponse: any,
  schemaData: DynamicSegmentSchemaDataType
): DynamicSegmentFormStateType => {
  // Helper function to find field in schema by field_id
  const findFieldInSchema = (fieldId: string): DynamicSegmentFieldType | null => {
    return schemaData.fields.find(field => field.id === fieldId) || null
  }

  // Helper function to find operator info for a field
  const findOperatorInfo = (
    field: DynamicSegmentFieldType,
    operatorKey: string
  ): DynamicSegmentOperatorInfoType | null => {
    return field.operators.find(op => op.key === operatorKey) || null
  }

  // Helper function to parse a single filter from audience definition
  const parseFilter = (filterData: any, index: number): DynamicSegmentFilterType | null => {
    const field = findFieldInSchema(filterData.target.field_id)
    if (!field) {
      console.warn(`Field not found in schema: ${filterData.target.field_id}`)
      return null
    }

    const operator = findOperatorInfo(field, filterData.operator)
    if (!operator) {
      console.warn(`Operator not found for field ${field.id}: ${filterData.operator}`)
      return null
    }

    const children: DynamicSegmentFilterType[] = []

    // Parse additional_metrics as children
    if (filterData.additional_metrics && Array.isArray(filterData.additional_metrics)) {
      filterData.additional_metrics.forEach((childData: any, childIndex: number) => {
        const childFilter = parseFilter(childData, childIndex)
        if (childFilter) {
          children.push(childFilter)
        }
      })
    }

    let values: string[] = Array.isArray(filterData.value)
      ? filterData.value.map((v: unknown) => (v === null || v === undefined ? '' : String(v)))
      : []

    const shouldDefaultTimesToOne =
      isEventMetricCountField(field) &&
      operator.dataType === 'NUMBER' &&
      operator.valueCount === 1 &&
      (values.length === 0 || values.every(v => isDynamicSegmentFilterValueSlotEmpty(v)))

    if (shouldDefaultTimesToOne) {
      values = ['1']
    }

    return {
      id: generateUUID(),
      field,
      operator,
      values,
      children
    }
  }

  // Parse filters from audience_definition
  const filters: DynamicSegmentFilterType[] = []
  if (segmentResponse.audience_definition?.and && Array.isArray(segmentResponse.audience_definition.and)) {
    segmentResponse.audience_definition.and.forEach((filterData: any, index: number) => {
      const filter = parseFilter(filterData, index)
      if (filter) {
        filters.push(filter)
      }
    })
  }

  // Parse preview filters from metadata
  const previewFilters: DynamicSegmentDimensionType[] =
    segmentResponse.metadata?.preview_filters || defaultPreviewFilters

  return {
    isActive: segmentResponse.active ?? true,
    name: segmentResponse.name || '',
    description: segmentResponse.description || '',
    tags: segmentResponse.tags || [],
    filters,
    previewFilters
  }
}

export const createCustomerTagSegmentPayload = (formState: CustomerTagSegmentFormStateType) => {
  const rules = formState.rules
    .map(rule => ({
      type: 'CUSTOMER_TAG' as const,
      action: rule.action,
      operator: rule.operator,
      values: parseCustomerTags(rule.raw)
    }))
    .filter(rule => rule.values.length > 0)

  return {
    name: formState.name,
    description: formState.description,
    segment_inclusion_rules: rules,
    active: formState.isActive,
    visible: true
  }
}

export const parseCustomerTags = (raw: string): string[] =>
  raw
    .split(/[\n,]/)
    .map(tag => tag.trim())
    .filter(Boolean)

export const parseCustomerTagSegmentConfig = (data: any): CustomerTagSegmentFormStateType => {
  const apiRules: any[] = Array.isArray(data?.segment_inclusion_rules) ? data.segment_inclusion_rules : []

  const tagRules = apiRules
    .filter(rule => rule?.type === 'CUSTOMER_TAG' && Array.isArray(rule?.values) && rule.values.length > 0)
    .map(rule => ({
      id: generateUUID(),
      action: (rule?.action === 'EXCLUDE' ? 'EXCLUDE' : 'INCLUDE') as CustomerTagRuleActionType,
      operator: (rule?.operator === 'AND' ? 'AND' : 'OR') as CustomerTagRuleOperatorType,
      raw: (rule.values as string[]).join(', ')
    }))

  const legacyTags: string[] = Array.isArray(data?.customer_tags) ? data.customer_tags : []

  return {
    name: data?.name ?? '',
    description: data?.description ?? '',
    rules:
      tagRules.length > 0
        ? tagRules
        : [
            {
              id: generateUUID(),
              action: 'INCLUDE' as CustomerTagRuleActionType,
              operator: 'OR' as CustomerTagRuleOperatorType,
              raw: legacyTags.join(', ')
            }
          ],
    isActive: data?.active ?? true
  }
}
